import $ from "jquery";
import {gsap} from "gsap/all";
import {Action} from "./Action";
import {cloneSvg} from "../SvgBank";
import {CircleTimer} from "../CircleTimer";

// Keep the drifting ship centered with the joystick. The joystick does not
// move the ship directly, it builds velocity: holding right makes the ship
// drift right faster and keep drifting after release, a light tap only nudges
// it. The run starts with a small drift toward a random side so the player has
// to counter it right away. After the run duration the ship has to sit within
// the center threshold to earn the point. What moves is the steering box of the
// ship, carrying the capsule, the burn around it and the target it is held in,
// plus the glass lens beside it, which is steered by the same numbers but has to
// stay out of that box (see the template for why). The trajectory lines stay
// put, they are what the ship is being lined up with. Alignment state classes go
// on the ship container so the circle and line strokes can color from CSS.
export class AlignShipAction extends Action {

  constructor(game, config) {
    super(game, config);

    // All positions are fractions of the screen width, measured from center.

    // How quickly holding the joystick builds velocity, in screen widths per
    // second squared. A longer hold means a faster drift after release.
    this.accel = 0.16;
    // Speed cap so long holds never make the ship uncontrollable.
    this.maxSpeed = 0.15;
    // Starting drift speed toward a random side, in screen widths per second.
    // High enough that the ship visibly wanders off before the player reacts.
    this.initialSpeed = 0.06;
    // Power curve softening the edges: outward motion runs at full speed near
    // the center and eases to a stop approaching the offset limit, instead of
    // hitting it like a wall. Higher keeps full speed longer before braking.
    this.edgeEasePower = 2;

    // Config from the JSON, with defaults: how long the run lasts, how close to
    // center counts as aligned, and how far the ship is allowed to wander.
    this.durationSec = config.duration !== undefined ? config.duration : 6;
    this.threshold = config.threshold !== undefined ? config.threshold : 0.05;
    this.maxOffset = config.max_offset !== undefined ? config.max_offset : 0.25;

    // The drift runs on a diagonal, bottom left to top right: the vertical
    // position follows the horizontal one, from this fraction of the screen
    // height down at the far left to as much up at the far right.
    this.maxRise = config.max_rise !== undefined ? config.max_rise : 0.015;

    this.$shipContainer = $("#ship-container");

    // The steering box of the one ship, holding the capsule and the target it
    // is held in, and the lens, which takes the same offset in the same units
    // but has to stay outside that box so it bends the path rather than the
    // ship. Steered as one set.
    this.$moving = this.app.getShip();
    this.$glassLens = this.$shipContainer.find(".ship-glass-lens");
    this.$steered = this.$moving.add(this.$glassLens);

    // The subtext follows the ship: one line while it is inside the parameters,
    // another asking for a correction while it is not. An action without the
    // second one keeps saying the same thing throughout.
    this.subtext = config.subtext;
    this.offTrackSubtext = config.subtext_off_track || config.subtext;

    this.pos = 0;
    this.vel = 0;
    this.elapsed = 0;
    this.aligned = null;
    this.running = false;
    this.holdCall = null;
    this.$joystick = null;

    this.tick = this.tick.bind(this);
  }

  start() {
    // The box being steered covers the whole screen, so its own size is the
    // screen size the position fractions map onto.
    this.screenWidth = this.$moving.width();
    this.screenHeight = this.$moving.height();

    gsap.set(this.$steered, {x: 0, y: 0});
    gsap.to(this.$glassLens, {opacity: 1});

    this.timer.start(this.durationSec);

    // The descent does not pause while the player steers, so the gauge travels
    // from the start state to the end state over the run instead of jumping
    // once the action is done.
    this.applyGaugeEnd(this.durationSec);

    // Start already drifting toward a random side so the player has to react.
    this.vel = (Math.random() < 0.5 ? -1 : 1) * this.initialSpeed;

    this.running = true;
    gsap.ticker.add(this.tick);
  }

  build() {
    // The joystick icon in the action container is static, the ship on screen
    // is the live part. The verdict overlays stack on top of the joystick.
    this.$joystick = $(`
      <div class="align-joystick stacked-button">
        <div class="base"></div>
        <div class="state success"></div>
        <div class="state fail"></div>
      </div>
    `);

    this.$joystick.find(".base").append(cloneSvg("align-joystick-button"));
    this.$joystick.find(".state.success").append(cloneSvg("button-success"));
    this.$joystick.find(".state.fail").append(cloneSvg("button-error"));

    this.$host.append(this.$joystick);

    // Same corner countdown the button actions use, so a run on the joystick
    // shows how long is left just as clearly. Created here so its face fades in
    // with the rest of the content.
    this.timer = new CircleTimer(this.game.$actionContainer.find(".corner-timer"));
  }

  tick(time, deltaMS) {
    if (!this.running) return;

    // Frozen while the "still there?" modal is up, so no time goes by and the
    // ship stays where the player left it.
    if (this.app.isGameplayPaused()) return;

    const dt = deltaMS / 1000;
    this.elapsed += dt;

    // The joystick accelerates the drift instead of moving the ship directly,
    // so the ship keeps its momentum once the stick is released.
    this.vel = constrain(this.vel + this.app.getAxis() * this.accel * dt, -this.maxSpeed, this.maxSpeed);

    // Outward motion eases off as the ship nears the offset limit, so it
    // slows into the edge instead of stopping dead. Inward motion is never
    // slowed, recovering always works at full speed.
    let moveVel = this.vel;
    if (this.pos * this.vel > 0) {
      const edge = Math.min(Math.abs(this.pos) / this.maxOffset, 1);
      moveVel *= 1 - Math.pow(edge, this.edgeEasePower);
    }
    this.pos = constrain(this.pos + moveVel * dt, -this.maxOffset, this.maxOffset);

    const x = this.pos * this.screenWidth;
    const y = -this.pos / this.maxOffset * this.maxRise * this.screenHeight;

    gsap.set(this.$steered, {x: x, y: y});

    // Live feedback while the run is going.
    this.setAligned(Math.abs(this.pos) <= this.threshold);

    if (this.elapsed >= this.durationSec) this.finish();
  }

  // Whether the ship is within the parameters right now, which colors the
  // circle and picks the line asking to hold the angle or to fix it. Written
  // only when it changes, since the tick runs every frame.
  setAligned(aligned) {
    if (aligned === this.aligned) return;
    this.aligned = aligned;

    this.$shipContainer.toggleClass("aligned", aligned).toggleClass("off-track", !aligned);
    this.game.setSubtext(aligned ? this.subtext : this.offTrackSubtext, !aligned);
  }

  finish() {
    this.running = false;
    gsap.ticker.remove(this.tick);

    // The run is over, so the target and the path it was flown against go
    // right away instead of waiting for the next transition.
    this.app.showAlignFrame(false);

    const success = Math.abs(this.pos) <= this.threshold;
    console.log(`align-ship final pos ${this.pos.toFixed(3)} / threshold ±${this.threshold} -> ${success ? "success" : "fail"}`);
    if (success) this.points++;

    this.$shipContainer
      .removeClass("aligned off-track")
      .addClass(success ? "success" : "fail");
    this.$joystick.addClass(success ? "success" : "fail");

    this.showVerdict(success);

    // Let the state read for a beat before moving on.
    this.holdCall = gsap.delayedCall(this.game.stateHoldSec, () => this.complete());
  }

  destroy() {
    this.running = false;
    gsap.ticker.remove(this.tick);
    if (this.holdCall) this.holdCall.kill();
    if (this.timer) {
      this.timer.destroy();
      this.timer = null;
    }

    this.$shipContainer.removeClass("aligned off-track success fail");

    // Back to center, and the lens goes back to hidden the way the CSS has it
    // at rest, since only this action shows it.
    gsap.to(this.$steered, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: "power1.inOut"
    });
    gsap.to(this.$glassLens, {
      opacity: 0,
      duration: 0.5,
      ease: "power1.inOut"
    });
  }
}
